import { sizedImageUrl } from '@akpl/shared';
import { type ReactElement, type ReactNode, useState } from 'react';

export interface CoverImageProps {
  src: string | null | undefined;
  /** Rendered instead of the image while there is no `src` or the `src` failed to load. */
  fallback: ReactNode;
  width?: number;
  height?: number;
  /**
   * Css width the picture is shown at, when `width` is not set (the size comes from CSS). The
   * media Worker is asked for the smallest thumbnail that covers it instead of the 500 px
   * original. Defaults to `width`, then `height`; nothing set → the original.
   */
  sizeHint?: number;
  className?: string;
}

/**
 * Artwork that disappears instead of painting a broken frame.
 *
 * Catalog images are seeded data and R2 objects go missing: the media Worker then answers
 * `404 application/json`, which Chrome refuses to render and logs as `ERR_BLOCKED_BY_ORB`
 * (QA TV-09 counted 18 of them per session). The `error` event hands over to the `fallback` —
 * the colour block with its note icon — with no console noise and no layout jump.
 *
 * The failure is remembered per URL, not as a flag: virtualized rows recycle their components,
 * so a card that inherits a new cover must try it instead of staying blank.
 */
export function CoverImage({
  src,
  fallback,
  width,
  height,
  sizeHint,
  className,
}: CoverImageProps): ReactElement {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!src || src === failedSrc) return <>{fallback}</>;
  const shown = sizedImageUrl(src, sizeHint ?? width ?? height ?? 0) ?? src;
  return (
    <img
      src={shown}
      alt=""
      width={width}
      height={height}
      loading="lazy"
      className={className}
      onError={() => setFailedSrc(src)}
    />
  );
}
